
// Production optimizations and performance utilities

// Debounce utility for search inputs and API calls
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle utility for scroll events and frequent updates
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Lazy loading utility for heavy components
export const lazyWithRetry = (importFunc: () => Promise<any>, retries = 3) => {
  return new Promise((resolve, reject) => {
    importFunc()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retries === 1) {
            reject(error);
            return;
          }
          lazyWithRetry(importFunc, retries - 1).then(resolve, reject);
        }, 1000);
      });
  });
};

// Memory cleanup for heavy data operations
export const cleanupHeavyData = (data: any[]) => {
  if (data && Array.isArray(data)) {
    data.length = 0; // Clear array without creating new reference
  }
};

// Batch processing utility for large datasets
export const batchProcess = async <T, R>(
  items: T[],
  processor: (batch: T[]) => Promise<R[]>,
  batchSize = 100
): Promise<R[]> => {
  const results: R[] = [];
  
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchResults = await processor(batch);
    results.push(...batchResults);
    
    // Allow UI to update between batches
    await new Promise(resolve => setTimeout(resolve, 0));
  }
  
  return results;
};

// Optimize images for better performance
export const optimizeImageUrl = (url: string, width?: number, quality = 80) => {
  if (!url) return '';
  
  // If using a CDN that supports query parameters for optimization
  const params = new URLSearchParams();
  if (width) params.set('w', width.toString());
  params.set('q', quality.toString());
  
  return url.includes('?') 
    ? `${url}&${params.toString()}`
    : `${url}?${params.toString()}`;
};

// Service worker registration for PWA - vite-plugin-pwa handles this automatically
export const registerServiceWorker = async () => {
  // vite-plugin-pwa registers the service worker automatically. Here we only
  // clean up any legacy, manually-registered service worker (sw.js).
  if (!('serviceWorker' in navigator)) return null;

  try {
    if (!import.meta.env.PROD) return null;

    // Clean up old manual service workers (sw.js) in favor of vite-plugin-pwa
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const reg of regs) {
      // Only unregister the old manual sw.js, not the new vite-pwa one
      if (reg.active?.scriptURL?.includes('sw.js')) {
        await reg.unregister();
        console.log('[PWA] Legacy service worker cleaned up');
      }
    }
    return null;
  } catch (_err) {
    return null;
  }
};

// Emergency asset recovery: on stylesheet load error (nosniff/MIME),
// clear caches and unregister SW once, then reload.
export const setupAssetRecovery = () => {
  if (!import.meta.env.PROD) return;
  let attempted = false;
  window.addEventListener('error', async (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const isLink = target.nodeName === 'LINK' && (target as HTMLLinkElement).rel === 'stylesheet';
    if (!isLink || attempted) return;
    attempted = true;
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.allSettled(keys.map(k => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.allSettled(regs.map(r => r.unregister()));
      }
    } finally {
      window.location.reload();
    }
  }, true);
};

// Performance monitoring
export const measurePerformance = (name: string) => {
  if (import.meta.env.PROD && 'performance' in window) {
    performance.mark(`${name}-start`);
    
    return () => {
      performance.mark(`${name}-end`);
      performance.measure(name, `${name}-start`, `${name}-end`);
    };
  }
  
  return () => {}; // No-op in development
};
